import { useState } from "react";
import type { FormEvent } from "react";
import { AssetKind } from "@xynex-money/shared";
import { api, ApiError } from "../api";
import { useCrudResource } from "../hooks/useCrudResource";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { MoneyInput } from "../components/MoneyInput";
import { DateInput } from "../components/DateInput";
import { fmtDate, fmtINR } from "../fmt";

const ASSET_KINDS = AssetKind.options;

interface Asset {
  id: string;
  kind: AssetKind;
  name: string;
  acquiredOn: string | null;
  cost: number | null;
  currentValue: number | null;
  notes: string | null;
}

interface Valuation {
  id: string;
  assetId: string;
  asOn: string;
  value: number;
}

interface FormState {
  kind: AssetKind;
  name: string;
  acquiredOn: string;
  cost: number | null;
  currentValue: number | null;
  notes: string;
}

const emptyForm: FormState = {
  kind: "other",
  name: "",
  acquiredOn: "",
  cost: null,
  currentValue: null,
  notes: "",
};

export function Assets() {
  const { items: assets, loading, error, setError, create, update, remove } = useCrudResource<Asset>("/api/assets");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Asset | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const [valAsset, setValAsset] = useState<Asset | null>(null);
  const [valuations, setValuations] = useState<Valuation[]>([]);
  const [valAsOn, setValAsOn] = useState("");
  const [valValue, setValValue] = useState<number | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (a: Asset) => {
    setEditing(a);
    setForm({
      kind: a.kind,
      name: a.name,
      acquiredOn: a.acquiredOn ?? "",
      cost: a.cost,
      currentValue: a.currentValue,
      notes: a.notes ?? "",
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      kind: form.kind,
      name: form.name,
      acquiredOn: form.acquiredOn || null,
      cost: form.cost,
      currentValue: form.currentValue,
      notes: form.notes || null,
    };
    try {
      if (editing) await update(editing.id, body);
      else await create(body);
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (a: Asset) => {
    if (!confirm(`Delete asset "${a.name}"?`)) return;
    try {
      await remove(a.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const openValuations = (a: Asset) => {
    setValAsset(a);
    setValAsOn("");
    setValValue(null);
    api
      .get<Valuation[]>(`/api/assets/${a.id}/valuations`)
      .then(setValuations)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load valuations"));
  };

  const onAddValuation = async (e: FormEvent) => {
    e.preventDefault();
    if (!valAsset || !valAsOn || valValue == null) return;
    try {
      const created = await api.post<Valuation>(`/api/assets/${valAsset.id}/valuations`, {
        asOn: valAsOn,
        value: valValue,
      });
      setValuations((prev) => [...prev, created].sort((a, b) => a.asOn.localeCompare(b.asOn)));
      setValAsOn("");
      setValValue(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to add valuation");
    }
  };

  const columns: Column<Asset>[] = [
    { key: "name", header: "Name" },
    { key: "kind", header: "Kind" },
    { key: "acquiredOn", header: "Acquired", render: (a) => fmtDate(a.acquiredOn) },
    { key: "cost", header: "Cost", align: "right", render: (a) => fmtINR(a.cost) },
    { key: "currentValue", header: "Current value", align: "right", render: (a) => fmtINR(a.currentValue) },
    {
      key: "actions",
      header: "",
      render: (a) => (
        <span className="actions-inline">
          <button type="button" onClick={() => openValuations(a)}>
            Valuations
          </button>
          <button type="button" onClick={() => openEdit(a)}>
            Edit
          </button>
          <button type="button" className="danger" onClick={() => onDelete(a)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h1>Assets</h1>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>
          Add asset
        </button>
      </div>
      <Table columns={columns} rows={assets} rowKey={(a) => a.id} emptyText={loading ? "Loading…" : "No assets yet."} />

      {modalOpen && (
        <Modal title={editing ? "Edit asset" : "Add asset"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="a-kind">Kind</label>
                <select id="a-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as FormState["kind"] })}>
                  {ASSET_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="a-name">Name</label>
                <input id="a-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="a-acq">Acquired on</label>
                <DateInput value={form.acquiredOn} onChange={(v) => setForm({ ...form, acquiredOn: v })} />
              </div>
              <div className="field">
                <label htmlFor="a-cost">Cost</label>
                <MoneyInput value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} />
              </div>
              <div className="field">
                <label htmlFor="a-cv">Current value</label>
                <MoneyInput value={form.currentValue} onChange={(v) => setForm({ ...form, currentValue: v })} />
              </div>
            </div>
            <div className="field">
              <label htmlFor="a-notes">Notes</label>
              <textarea id="a-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {valAsset && (
        <Modal title={`Valuations: ${valAsset.name}`} onClose={() => setValAsset(null)}>
          <div className="modal-body">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>As on</th>
                    <th className="num">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {valuations.map((v) => (
                    <tr key={v.id}>
                      <td>{fmtDate(v.asOn)}</td>
                      <td className="num">{fmtINR(v.value)}</td>
                    </tr>
                  ))}
                  {valuations.length === 0 && (
                    <tr>
                      <td colSpan={2} className="empty-state">
                        No valuation history yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <form onSubmit={onAddValuation} className="toolbar" style={{ marginTop: "0.75rem" }}>
              <div className="field">
                <label htmlFor="v-ason">As on</label>
                <DateInput value={valAsOn} onChange={setValAsOn} required />
              </div>
              <div className="field">
                <label htmlFor="v-value">Value</label>
                <MoneyInput value={valValue} onChange={setValValue} required />
              </div>
              <button className="primary" type="submit">
                Add
              </button>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
